import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search, Star, Volume2 } from "lucide-react";
import type { ModelInfo } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Props {
  models: ModelInfo[];
  selected: string | null;
  favorite: string | null;
  favorites?: string[];
  onSelect: (key: string) => void;
  onFavoriteChange: (key: string | null) => void;
  onFavoritesChange?: (keys: string[]) => void;
  placement?: "top" | "bottom";
  compact?: boolean;
  showFavorite?: boolean;
}

const PROVIDER_STYLE: Record<string, { dot: string; label: string }> = {
  google: { dot: "bg-sky-400", label: "Google" },
  openai: { dot: "bg-emerald-400", label: "OpenAI" },
  sync: { dot: "bg-cyan-400", label: "Sync Labs" },
  fal: { dot: "bg-fuchsia-400", label: "fal" },
  byteplus_ark: { dot: "bg-rose-400", label: "BytePlus" },
  topaz: { dot: "bg-amber-400", label: "Topaz" },
  elevenlabs: { dot: "bg-violet-400", label: "ElevenLabs" },
  runway: { dot: "bg-lime-400", label: "Runway" },
};

function providerStyle(provider: string) {
  return PROVIDER_STYLE[provider] ?? { dot: "bg-studio-muted", label: provider };
}

function compactCompatibility(model: ModelInfo): string[] {
  const compatibility = model.compatibility;
  if (!compatibility) return [];
  const labels: string[] = [];
  if (compatibility.resolutions.length) labels.push(compatibility.resolutions.slice(-2).join(" / "));
  if (compatibility.qualities?.length) labels.push(compatibility.qualities.slice(-1)[0]);
  if (compatibility.durations.length) {
    const duration = String(compatibility.durations.slice(-1)[0]);
    labels.push(duration.toLowerCase() === "auto" ? "Auto" : /s$/i.test(duration) ? duration : `${duration}s`);
  }
  if (compatibility.reference_types.length) labels.push("References");
  if (compatibility.audio) labels.push("Audio");
  return labels.slice(0, 3);
}

export function ModelPicker({
  models,
  selected,
  favorite,
  favorites,
  onSelect,
  onFavoriteChange,
  onFavoritesChange,
  placement = "bottom",
  compact = false,
  showFavorite = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedModel = models.find((model) => model.key === selected);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return models;
    return models.filter((model) =>
      [model.display_name, model.provider, model.family, model.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [models, query]);

  const favoriteKeys = favorites?.length ? favorites : favorite ? [favorite] : [];
  const starred = favoriteKeys
    .map((key) => filtered.find((model) => model.key === key))
    .filter((model): model is ModelInfo => Boolean(model));
  const featured = filtered.filter(
    (model) => !starred.some((item) => item.key === model.key) && Boolean(model.tier_config?.badge)
  );
  const remaining = filtered.filter(
    (model) =>
      !featured.some((item) => item.key === model.key)
      && !starred.some((item) => item.key === model.key)
  );
  const providerGroups = Object.entries(
    remaining.reduce<Record<string, ModelInfo[]>>((groups, model) => {
      (groups[model.provider] ??= []).push(model);
      return groups;
    }, {})
  ).sort(([a], [b]) => providerStyle(a).label.localeCompare(providerStyle(b).label));

  const selectedIsFavorite = Boolean(selected && favoriteKeys.includes(selected));

  const toggleFavorite = (key: string) => {
    if (!onFavoritesChange) {
      onFavoriteChange(selectedIsFavorite ? null : key);
      return;
    }
    const next = selectedIsFavorite
      ? favoriteKeys.filter((item) => item !== key)
      : [key, ...favoriteKeys];
    onFavoritesChange(next);
    // The single-favourite field still drives the Settings summary and the
    // startup fallback, so it follows the head of the list.
    onFavoriteChange(next[0] ?? null);
  };

  const choose = (key: string) => {
    onSelect(key);
    setOpen(false);
    setQuery("");
  };

  const renderModel = (model: ModelInfo) => {
    const style = providerStyle(model.provider);
    const active = selected === model.key;
    const badges = compactCompatibility(model);
    return (
      <button
        key={model.key}
        type="button"
        role="option"
        aria-selected={active}
        onClick={() => choose(model.key)}
        className={cn(
          "flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
          active
            ? "border-studio-accent/45 bg-studio-accent-soft"
            : "border-transparent hover:border-studio-border hover:bg-studio-card"
        )}
      >
        <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", style.dot)} />
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="truncate text-xs font-semibold text-studio-ink">{model.display_name}</span>
            {model.tier_config?.badge ? (
              <span className="rounded bg-studio-accent/12 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-studio-accent">
                {String(model.tier_config.badge)}
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block line-clamp-1 text-[10px] text-studio-muted">
            {model.description || `${style.label} · ${model.family}`}
          </span>
          {badges.length > 0 && (
            <span className="mt-1.5 flex flex-wrap gap-1">
              {badges.map((badge) => (
                <span key={badge} className="rounded-md border border-studio-border bg-studio-panel px-1.5 py-0.5 text-[9px] text-studio-muted">
                  {badge}
                </span>
              ))}
            </span>
          )}
        </span>
        {model.compatibility?.audio && <Volume2 className="mt-1 h-3.5 w-3.5 shrink-0 text-studio-muted" aria-label="Audio supported" />}
        {favoriteKeys.includes(model.key) && <Star className="mt-1 h-3.5 w-3.5 shrink-0 fill-current text-studio-accent" aria-label="Favorite model" />}
        {active && <Check className="mt-1 h-4 w-4 shrink-0 text-studio-accent" />}
      </button>
    );
  };

  return (
    <div ref={ref} className="relative min-w-0">
      <div className="flex min-w-0 gap-1.5">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={cn(
            "group flex min-w-0 flex-1 items-center gap-2 rounded-lg border bg-studio-card text-left transition-colors hover:bg-studio-card-hover focus-visible:outline-none focus-visible:shadow-glow-sm",
            compact ? "min-h-9 px-2.5 py-1.5" : "px-3.5 py-3",
            open ? "border-studio-accent/55" : "border-studio-border"
          )}
        >
          {selectedModel && <span className={cn("h-2 w-2 shrink-0 rounded-full", providerStyle(selectedModel.provider).dot)} />}
          <span className="min-w-0 flex-1">
            <span className={cn("block truncate font-semibold text-studio-ink", compact ? "text-[11px]" : "text-sm")}>
              {selectedModel?.display_name ?? "Select model"}
            </span>
            {!compact && selectedModel && (
              <span className="block truncate text-[10px] text-studio-muted">
                {providerStyle(selectedModel.provider).label} · {selectedModel.family}
              </span>
            )}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-studio-muted" />
        </button>
        {showFavorite && (
          <button
            type="button"
            disabled={!selectedModel}
            aria-label={selectedIsFavorite ? "Remove favorite model" : "Set favorite model"}
            aria-pressed={selectedIsFavorite}
            onClick={() => selected && toggleFavorite(selected)}
            className={cn(
              "flex shrink-0 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm disabled:opacity-40",
              compact ? "w-9" : "w-11",
              selectedIsFavorite
                ? "border-studio-accent/35 bg-studio-accent-soft text-studio-accent"
                : "border-studio-border bg-studio-card text-studio-muted hover:text-studio-accent"
            )}
          >
            <Star className={cn("h-3.5 w-3.5", selectedIsFavorite && "fill-current")} />
          </button>
        )}
      </div>

      {open && (
        <div
          className={cn(
            "absolute left-0 z-50 w-[min(42rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-studio-border-light bg-studio-panel shadow-panel animate-rise",
            placement === "top" ? "bottom-full mb-2" : "top-full mt-2"
          )}
        >
          <div className="border-b border-studio-border p-2.5">
            <label className="flex items-center gap-2 rounded-lg border border-studio-border bg-studio-card px-3 focus-within:border-studio-accent/55">
              <Search className="h-3.5 w-3.5 text-studio-muted" />
              <span className="sr-only">Search models</span>
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search model or provider…"
                className="min-h-10 min-w-0 flex-1 bg-transparent text-xs text-studio-ink outline-none placeholder:text-studio-muted"
              />
            </label>
          </div>
          <div role="listbox" aria-label="Available models" className="max-h-[min(28rem,60vh)] overflow-y-auto p-2">
            {starred.length > 0 && (
              <section aria-label="Favorite models">
                <p className="px-2 pb-1 pt-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-accent">Favourites</p>
                <div className="space-y-0.5">{starred.map(renderModel)}</div>
              </section>
            )}
            {featured.length > 0 && (
              <section aria-label="Featured models" className="mt-2 first:mt-0">
                <p className="px-2 pb-1 pt-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Featured</p>
                <div className="space-y-0.5">{featured.map(renderModel)}</div>
              </section>
            )}
            {providerGroups.map(([provider, providerModels]) => (
              <section key={provider} aria-label={`${providerStyle(provider).label} models`} className="mt-2 first:mt-0">
                <p className="px-2 pb-1 pt-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-studio-muted">
                  {providerStyle(provider).label}
                </p>
                <div className="space-y-0.5">{providerModels.map(renderModel)}</div>
              </section>
            ))}
            {filtered.length === 0 && <p className="px-3 py-8 text-center text-xs text-studio-muted">No matching models</p>}
          </div>
        </div>
      )}
    </div>
  );
}
